/**
 * Alat cutoff & sumatif — ketiga ambang yang memutuskan net pay.
 *
 * Sebangun dengan kelima alat sebelumnya, dan dipakai oleh dua layar yang
 * sama: rel parameter di ruang kerja dan tahap keenam di rel alur
 * deterministik.
 *
 * Yang khas pada tahap ini adalah bahwa ia satu-satunya yang tidak memilih
 * metode. Tidak ada persamaan yang bercabang di sini — yang ada hanyalah tiga
 * ambang, dan justru karena itu ketiganya tampak sederajat di rel lama: tiga
 * penggeser yang serupa, berjajar, tanpa satu kata pun tentang apa yang
 * diputuskan masing-masing. Dua di antaranya adalah batas atas dan yang di
 * tengah sebuah batas bawah; dua di antaranya menghasilkan net reservoir dan
 * yang ketiga memilih net pay dari net reservoir itu. Ketiganya juga tidak
 * pernah bisa dimatikan bagi peran baca-saja, sebab penggeser di aplikasi ini
 * belum pernah punya keadaan nonaktif — satu-satunya kendali hitung di rel
 * yang begitu.
 */
import { useState } from "react";

import type { ParameterValues } from "../types";
import { ComputedInputs, sameParam } from "./CurveSource";
import { Button, Modal, NumberField, ParamCaption } from "./ui";

/**
 * Kunci yang boleh berangkat dari jendela ini; sisanya bukan urusan cutoff.
 *
 * Batas zona tidak ada di sini meskipun sumatifnya dijalankan atas zona:
 * puncak dan dasar tiap zona adalah data sumur, bukan parameter tafsir, dan
 * ia disunting di tempat marker disunting. Sebuah jendela yang boleh
 * memindahkan marker dari layar analisis adalah dua tempat yang bisa
 * berselisih tentang kedalaman yang sama.
 */
const CUTOFF_KEYS: (keyof ParameterValues)[] = [
  "cutoff_vsh", "cutoff_phie", "cutoff_sw",
];

/**
 * Ketiga ambangnya, terkelompok menurut angka yang dihasilkannya.
 *
 * Bukan menurut kurvanya, yang akan menempatkan ketiganya dalam satu daftar
 * datar sebagaimana rel lama menempatkannya. Yang membelah ketiganya adalah
 * apa yang diputuskan: Vsh dan PHIE menyaring batuannya menjadi net reservoir,
 * dan SW memilih net pay dari net reservoir itu. Pembelahan itu pula yang
 * menjelaskan mengapa net pay tidak pernah bisa melebihi net reservoir — hal
 * yang di rel lama harus ditebak dari tiga penggeser yang tampak sederajat.
 */
export function CutoffFields({
  params,
  onChange,
  disabled = false,
}: {
  params: ParameterValues;
  onChange: (
    key: keyof ParameterValues,
    value: number | string | boolean | Record<string, string>,
  ) => void;
  disabled?: boolean;
}) {
  return (
    <>
      <ParamCaption>Net reservoir — rock quality</ParamCaption>
      {/* Namanya menyebut arahnya. Ketiganya dulu bernama "Cutoff Vsh",
          "Cutoff PHIE" dan "Cutoff SW" — tiga nama dengan bentuk yang sama
          persis bagi dua ambang atas dan satu ambang bawah, dan arah yang
          keliru pada PHIE membalik seluruh net reservoir tanpa satu pesan
          galat pun. */}
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8 }}>
        <NumberField
          label="Max Vsh"
          value={Number(params.cutoff_vsh)}
          step={0.01}
          min={0}
          max={1}
          onChange={(v) => onChange("cutoff_vsh", v)}
          disabled={disabled}
        />
        <NumberField
          label="Min PHIE"
          value={Number(params.cutoff_phie)}
          step={0.005}
          min={0}
          max={1}
          onChange={(v) => onChange("cutoff_phie", v)}
          disabled={disabled}
        />
      </div>
      <span style={{ font: "400 9.5px var(--font-sans)", color: "var(--ink-6)", lineHeight: 1.5 }}>
        Rock clean enough and porous enough to flow. The two run in opposite directions
        — shale is capped from above, porosity from below — and a sample whose Vsh or
        PHIE is blank never passes.
      </span>

      <ParamCaption>Net pay — hydrocarbon</ParamCaption>
      <NumberField
        label="Max SW"
        value={Number(params.cutoff_sw)}
        step={0.01}
        min={0}
        max={1}
        onChange={(v) => onChange("cutoff_sw", v)}
        disabled={disabled}
      />
      <span style={{ font: "400 9.5px var(--font-sans)", color: "var(--ink-6)", lineHeight: 1.5 }}>
        Picked from net reservoir, not from gross: net pay is always part of net
        reservoir and never exceeds it. A sample without SW — the whole well, when RT is
        missing — never becomes net pay even where the rock passes, and moving this
        threshold will not bring it back.
      </span>
      <ComputedInputs
        rows={[
          { curve: "VSH", from: "computed by stage 1 on this same run" },
          { curve: "PHIE", from: "computed by stage 3" },
          { curve: "SW", from: "computed by stage 4" },
        ]}
      />

    </>
  );
}

/**
 * Tahap cutoff & sumatif sebagai jendelanya sendiri.
 *
 * Aturannya sama dengan kelima jendela sebelumnya: yang dikirim pulang
 * hanyalah yang benar-benar diubah, sebab tiap run dengan override
 * menerbitkan versi parameter set baru.
 */
export function CutoffDialog({
  params,
  busy = false,
  disabled = false,
  onClose,
  onRun,
}: {
  params: ParameterValues;
  busy?: boolean;
  disabled?: boolean;
  onClose: () => void;
  onRun: (overrides: Partial<ParameterValues>) => void;
}) {
  const [draft, setDraft] = useState<Partial<ParameterValues>>({});
  const merged = { ...params, ...draft } as ParameterValues;
  const changed = Object.fromEntries(
    CUTOFF_KEYS.filter((key) => key in draft && !sameParam(draft[key], params[key])).map(
      (key) => [key, draft[key]],
    ),
  ) as Partial<ParameterValues>;

  return (
    <Modal
      title="6 · Cutoff & summation"
      width={440}
      onClose={onClose}
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>
            Cancel
          </Button>
          <Button
            variant="primary"
            busy={busy}
            disabled={disabled}
            onClick={() => onRun(changed)}
          >
            Apply cutoffs
          </Button>
        </>
      }
    >
      <CutoffFields
        params={merged}
        onChange={(key, value) => setDraft((current) => ({ ...current, [key]: value }))}
        disabled={disabled || busy}
      />
      <span style={{ font: "400 9.5px/1.5 var(--font-sans)", color: "var(--ink-6)" }}>
        {"The whole deterministic workflow is run again. These three thresholds " +
          "hold for the whole well rather than per zone: every zone is summarised " +
          "with the same triplet. What changes is not only net pay and HCPV — the " +
          "mean PHIE, SW and Vsh of each zone are computed over net pay samples " +
          "alone, so moving a cutoff moves every mean the zone sheet reports, and " +
          "with them the mean k of stage 5."}
      </span>
    </Modal>
  );
}
